import { CdkDropList } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TreatmentStepsFacade } from '@principle-theorem/ng-clinical-charting/store';
import {
  ScrollContainer,
  ScrollContainerManagerService,
  StafferSettingsStoreService,
} from '@principle-theorem/ng-principle-shared';
import { TrackByFunctions } from '@principle-theorem/ng-shared';
import { ChartedTreatmentGroup } from '@principle-theorem/principle-core';
import {
  type IChartedMultiStepTreatment,
  type IAppointment,
  type IChartedMultiStepTreatmentStep,
  type IChartedRef,
  type IChartedTreatment,
  type IChartedTreatmentGroup,
  isChartedTreatmentGroup,
  type ITreatmentCategory,
  type ITreatmentPlan,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  isWithRef,
  multiMap,
  shareReplayCold,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, get, groupBy } from 'lodash';
import {
  combineLatest,
  iif,
  type Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { type IEditChartableData } from '../../chartable-surface-updater';
import {
  isCrossPlanTreatmentMove,
  type ITreatmentDragDropNode,
  TreatmentDragDropGroup,
  type TreatmentItem,
} from '../../treatment-drag-drop-group';

@Component({
  selector: 'pr-treatment-scope-list',
  templateUrl: './treatment-scope-list.component.html',
  styleUrls: ['./treatment-scope-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TreatmentScopeListComponent implements OnDestroy {
  private _cdkDropList$: ReplaySubject<CdkDropList<TreatmentItem>> =
    new ReplaySubject(1);
  private _registrationRef$ = this._cdkDropList$.pipe(map((list) => [list]));
  private _onDestroy$: Subject<void> = new Subject();
  trackByTreatment = TrackByFunctions.uniqueId<IChartedTreatment>();
  trackByTreatmentGroup = TrackByFunctions.uniqueId<IChartedTreatmentGroup>();
  @Input() disabled = false;
  @Input() compact = false;
  @Input() expanded = false;
  @Input() appointment?: WithRef<IAppointment>;
  selectedSurfaces$ = new ReplaySubject<Partial<IChartedRef>[]>(1);
  allTreatments$ = new ReplaySubject<IChartedTreatment[]>(1);
  step$ = new ReplaySubject<
    WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep
  >(1);
  plan$ = new ReplaySubject<
    WithRef<ITreatmentPlan> | IChartedMultiStepTreatment
  >(1);
  treatments$: Observable<IChartedTreatment[]>;
  treatmentGroups$: Observable<IChartedTreatmentGroup[]>;
  scrollContainer$: Observable<HTMLElement>;
  treatmentGroupDragDrop: TreatmentDragDropGroup;
  listData$: Observable<ITreatmentDragDropNode>;
  isGrouped = signal<boolean>(false);
  @Output() treatmentsChange = new EventEmitter<IChartedTreatment[]>();
  @Output() treatmentDeleted = new EventEmitter<IChartedTreatment[]>();
  @Output() categoryChanged = new EventEmitter<WithRef<ITreatmentCategory>>();
  @Output() updateChartable = new EventEmitter<IEditChartableData>();

  @ViewChild(CdkDropList, { static: true })
  set cdkDropList(cdkDropList: CdkDropList) {
    if (cdkDropList) {
      this._cdkDropList$.next(cdkDropList);
    }
  }

  @Input()
  set treatments(treatments: IChartedTreatment[]) {
    if (treatments) {
      this.allTreatments$.next(treatments);
    }
  }

  @Input()
  set selectedSurfaces(selectedSurfaces: Partial<IChartedRef>[]) {
    if (selectedSurfaces) {
      this.selectedSurfaces$.next(selectedSurfaces);
    }
  }

  @Input()
  set step(step: WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep) {
    if (step) {
      this.step$.next(step);
    }
  }

  @Input()
  set plan(plan: WithRef<ITreatmentPlan> | IChartedMultiStepTreatment) {
    if (plan) {
      this.plan$.next(plan);
    }
  }

  constructor(
    private _treatmentStepsFacade: TreatmentStepsFacade,
    private _scrollContainerManager: ScrollContainerManagerService,
    private _route: ActivatedRoute,
    private _stafferSettings: StafferSettingsStoreService
  ) {
    this.scrollContainer$ = this._scrollContainerManager.getContainer(
      ScrollContainer.InAppointment
    );

    const isGrouped$ = this._stafferSettings.treatmentPlan$.pipe(
      map((settings) => settings.groupedTreatments ?? false)
    );

    isGrouped$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((isGrouped) => this.isGrouped.set(isGrouped));

    const singleTreatmentsByConfig$ = this.allTreatments$.pipe(
      map((allTreatments) =>
        Object.values(
          groupBy(allTreatments, (treatment) => treatment.config.ref.path)
        )
          .filter((treatments) => treatments.length === 1)
          .flat()
      ),
      shareReplayCold()
    );

    const groupedTreatmentsByConfig$ = this.allTreatments$.pipe(
      map((allTreatments) =>
        Object.values(
          groupBy(allTreatments, (treatment) => treatment.config.ref.path)
        ).filter((treatments) => treatments.length > 1)
      ),
      multiMap((treatments) => {
        if (!treatments.length) {
          return;
        }

        const config = treatments[0].config;
        const chartedSurfaces = treatments
          .map((treatment) => treatment.chartedSurfaces)
          .flat();

        return ChartedTreatmentGroup.init({
          uuid: config.ref.id,
          config,
          treatments,
          chartedSurfaces,
        });
      }),
      map(compact)
    );

    this.treatments$ = isGrouped$.pipe(
      distinctUntilChanged(),
      switchMap((isGrouped) =>
        iif(() => isGrouped, singleTreatmentsByConfig$, this.allTreatments$)
      )
    );

    this.treatmentGroups$ = isGrouped$.pipe(
      distinctUntilChanged(),
      switchMap((isGrouped) =>
        iif(() => isGrouped, groupedTreatmentsByConfig$, of([]))
      )
    );

    this.listData$ = combineLatest([
      this.plan$.pipe(startWith(undefined)),
      this.step$.pipe(startWith(undefined)),
      this.treatments$,
      this.treatmentGroups$,
    ]).pipe(
      map(([plan, step, treatments, groups]) => ({
        parent: {
          plan,
          step,
        },
        items: [...treatments, ...groups],
      }))
    );

    this.treatmentGroupDragDrop = get(
      this._route.snapshot.data,
      'treatmentGroupDragDrop',
      new TreatmentDragDropGroup()
    ) as TreatmentDragDropGroup;
    this.treatmentGroupDragDrop.register(this._registrationRef$);

    combineLatest([this.treatmentGroupDragDrop.afterDrop$, this._cdkDropList$])
      .pipe(
        map(([afterDrop, dropList]) => {
          const isSourceList = afterDrop.previous.id === dropList.id;
          const isTargetList = afterDrop.current.id === dropList.id;

          if (!isSourceList && !isTargetList) {
            return;
          }
          return {
            afterDrop,
            isSourceList,
            node: isSourceList
              ? afterDrop.previous.data
              : afterDrop.current.data,
          };
        }),
        filterUndefined(),
        takeUntil(this._onDestroy$)
      )
      .subscribe(async ({ afterDrop, isSourceList, node }) => {
        const isCrossPlan = isCrossPlanTreatmentMove(
          afterDrop.previous.data.parent,
          afterDrop.current.data.parent
        );

        if (isSourceList && isCrossPlan) {
          return;
        }

        if (this._shouldHandleCrossPlanMove(afterDrop, isSourceList)) {
          await this._handleCrossPlanMove(afterDrop);
          return;
        }

        this.updateOrder(node);
      });

    this.treatmentGroupDragDrop.nestedItemRemoved$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(({ item }) => this.handleNestedItemRemoved(item));
  }

  ngOnDestroy(): void {
    this.treatmentGroupDragDrop.unregister(this._registrationRef$);
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  updateOrder(node: ITreatmentDragDropNode): void {
    const expandedItems = node.items.flatMap(
      (item: IChartedTreatment | IChartedTreatmentGroup) => {
        if (isChartedTreatmentGroup(item)) {
          return item.treatments;
        }
        return [item];
      }
    );

    this.treatmentsChange.next(expandedItems);
  }

  updateTreatment(
    allTreatments: IChartedTreatment[],
    updatedTreatments: IChartedTreatment | IChartedTreatment[]
  ): void {
    const updatedTreatmentsArray = Array.isArray(updatedTreatments)
      ? updatedTreatments
      : [updatedTreatments];

    const updatesById = new Map(
      updatedTreatmentsArray.map((treatment) => [treatment.uuid, treatment])
    );

    const changed = allTreatments.map((currentTreatment) => {
      const update = updatesById.get(currentTreatment.uuid);
      if (!update) {
        return currentTreatment;
      }
      return {
        ...currentTreatment,
        ...update,
      };
    });

    this.treatmentsChange.next(changed);
  }

  deleteTreatments(treatments: IChartedTreatment | IChartedTreatment[]): void {
    const deletedTreatmentsArray = Array.isArray(treatments)
      ? treatments
      : [treatments];

    this.treatmentDeleted.emit(deletedTreatmentsArray);
  }

  enterIfEnabledPredicateFn(): boolean {
    return !this.disabled;
  }

  handleNestedItemRemoved(item: IChartedTreatment): void {
    this.allTreatments$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((allTreatments) => {
        const updatedAll = allTreatments.filter(
          (treatment) => treatment.uuid !== item.uuid
        );

        this.treatmentsChange.emit(updatedAll);
      });
  }

  private _shouldHandleCrossPlanMove(
    afterDrop: {
      previous: CdkDropList<ITreatmentDragDropNode>;
      current: CdkDropList<ITreatmentDragDropNode>;
      item: TreatmentItem;
    },
    isSourceList: boolean
  ): boolean {
    if (isSourceList || isChartedTreatmentGroup(afterDrop.item)) {
      return false;
    }

    return isCrossPlanTreatmentMove(
      afterDrop.previous.data.parent,
      afterDrop.current.data.parent
    );
  }

  private async _handleCrossPlanMove(afterDrop: {
    previous: CdkDropList<ITreatmentDragDropNode>;
    current: CdkDropList<ITreatmentDragDropNode>;
    item: TreatmentItem;
  }): Promise<void> {
    const sourceStep = afterDrop.previous.data.parent?.step;
    const targetStep = afterDrop.current.data.parent?.step;

    if (
      isChartedTreatmentGroup(afterDrop.item) ||
      !sourceStep ||
      !targetStep ||
      !isWithRef(sourceStep) ||
      !isWithRef(targetStep)
    ) {
      return;
    }

    const targetItems = afterDrop.current.data.items;
    const targetIndex = targetItems.findIndex(
      (item) => 'uuid' in item && item.uuid === afterDrop.item.uuid
    );

    await this._treatmentStepsFacade.moveTreatmentBetweenPlans({
      treatment: afterDrop.item,
      sourceStep,
      targetStep,
      targetIndex: targetIndex >= 0 ? targetIndex : undefined,
    });
  }
}
